//----------------------------------------------------------------------------------------------------------------------
// Built-in Debug Console Commands
//
// Registers default references and commands onto the debug console namespace, providing convenient
// access to game engine internals from the browser dev console.
//----------------------------------------------------------------------------------------------------------------------

/* eslint-disable no-console */

import { DebugConsole } from '../classes/debugConsole.ts';
import { createEntityProxy } from '../utils/entityProxy.ts';

// Types
import type { GameEngine } from '../classes/gameEngine.ts';
import type { GameEntity } from '../classes/entity.ts';
import type { Unsubscribe } from '../classes/eventBus.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Resolves an entity from a name string or returns the entity if already a GameEntity instance.
 */
function resolveEntity(
    nameOrEntity : string | GameEntity,
    ge : GameEngine
) : GameEntity | undefined
{
    if(typeof nameOrEntity === 'string')
    {
        return ge.managers.entityManager.getByName(nameOrEntity);
    }
    return nameOrEntity;
}

/**
 * Collects entities for collider commands. No arg = all entities, string = try name then type,
 * entity instance = direct.
 */
function resolveEntitiesForCollider(
    nameOrTypeOrEntity : string | GameEntity | undefined,
    ge : GameEngine
) : GameEntity[]
{
    const em = ge.managers.entityManager;

    if(nameOrTypeOrEntity === undefined)
    {
        return [ ...em.getAllEntities() ];
    }

    if(typeof nameOrTypeOrEntity !== 'string')
    {
        return [ nameOrTypeOrEntity ];
    }

    // Try name first
    const byName = em.getByName(nameOrTypeOrEntity);
    if(byName)
    {
        return [ byName ];
    }

    // Then try type
    const byType = em.getByType(nameOrTypeOrEntity);
    if(byType.length > 0)
    {
        return byType;
    }

    console.warn(`[sage] No entity found with name or type "${ nameOrTypeOrEntity }"`);
    return [];
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Registers all default references and commands onto the debug console namespace.
 */
export function registerBuiltins(dc : DebugConsole, ge : GameEngine) : void
{
    const em = ge.managers.entityManager;
    const gm = ge.managers.gameManager;
    const lm = ge.managers.levelManager;

    // Track active event loggers in a closure
    const activeLoggers : Unsubscribe[] = [];

    //------------------------------------------------------------------------------------------------------------------
    // Exposed references
    //------------------------------------------------------------------------------------------------------------------

    dc.expose('engine', ge);
    dc.expose('events', ge.eventBus);
    dc.expose('physics', ge.physics);
    dc.expose('colliders', ge.debug.colliders);
    dc.expose('entities', createEntityProxy(em));

    // Dynamic getters
    dc.expose('level', { get: () => lm.currentLevel });
    dc.expose('scene', { get: () => lm.currentLevel?.scene ?? null });

    //------------------------------------------------------------------------------------------------------------------
    // Commands
    //------------------------------------------------------------------------------------------------------------------

    dc.expose('tp', (nameOrEntity : string | GameEntity, x : number, y : number, z : number) =>
    {
        const entity = resolveEntity(nameOrEntity, ge);
        if(!entity)
        {
            console.warn(`[sage] Entity not found: ${ nameOrEntity }`);
            return;
        }

        if(!entity.node)
        {
            console.warn(`[sage] Entity "${ entity.name ?? entity.id }" has no node`);
            return;
        }

        entity.node.position.x = x;
        entity.node.position.y = y;
        entity.node.position.z = z;
        console.log(`[sage] Teleported "${ entity.name ?? entity.id }" to (${ x }, ${ y }, ${ z })`);
    });

    dc.expose('spawn', async(type : string, x : number, y : number, z : number) =>
    {
        const entity = await em.createEntity(type, {
            initialState: { position: { x, y, z } },
        });
        console.log(`[sage] Spawned "${ type }" (${ entity.id }) at (${ x }, ${ y }, ${ z })`);
        return entity;
    });

    dc.expose('list', (type ?: string) =>
    {
        let entities : GameEntity[];

        if(type)
        {
            entities = em.getByType(type);
        }
        else
        {
            entities = [ ...em.getAllEntities() ];
        }

        const rows = entities.map((ent) => ({
            id: ent.id,
            type: ent.type,
            name: ent.name ?? '(unnamed)',
            node: ent.node?.name ?? '(no node)',
        }));

        console.table(rows);
        return entities;
    });

    dc.expose('inspect', (nameOrEntity : string | GameEntity) =>
    {
        const entity = resolveEntity(nameOrEntity, ge);
        if(!entity)
        {
            console.warn(`[sage] Entity not found: ${ nameOrEntity }`);
            return;
        }

        const info = {
            id: entity.id,
            type: entity.type,
            name: entity.name,
            state: entity.state,
            behaviors: entity.behaviors.map((bh) => bh.name),
            node: entity.node?.name ?? null,
            position: entity.node?.position ?? null,
        };

        console.log('[sage] Entity info:', info);
        return info;
    });

    dc.expose('showColliders', (nameOrTypeOrEntity ?: string | GameEntity) =>
    {
        const entities = resolveEntitiesForCollider(nameOrTypeOrEntity, ge);
        for(const entity of entities)
        {
            entity.showCollider();
        }
        console.log(`[sage] Showing colliders for ${ entities.length } entity(s)`);
    });

    dc.expose('hideColliders', (nameOrTypeOrEntity ?: string | GameEntity) =>
    {
        const entities = resolveEntitiesForCollider(nameOrTypeOrEntity, ge);
        for(const entity of entities)
        {
            entity.hideCollider();
        }
        console.log(`[sage] Hiding colliders for ${ entities.length } entity(s)`);
    });

    dc.expose('logEvents', (pattern ?: string | RegExp) =>
    {
        const resolvedPattern = pattern ?? '*';
        interface EventSub
        {
            subscribe : (
                pat : string | RegExp,
                cb : (event : { type : string; payload ?: unknown }) => void
            ) => Unsubscribe;
        }

        const unsub = (ge.eventBus as unknown as EventSub).subscribe(resolvedPattern, (event) =>
        {
            console.log(`[sage] Event: ${ event.type }`, event.payload);
        });

        activeLoggers.push(unsub);
        console.log(`[sage] Logging events matching "${ resolvedPattern }"`);
        return unsub;
    });

    dc.expose('stopLogging', () =>
    {
        for(const unsub of activeLoggers)
        {
            unsub();
        }

        console.log(`[sage] Stopped ${ activeLoggers.length } event logger(s)`);
        activeLoggers.length = 0;
    });

    dc.expose('pause', () =>
    {
        gm.pause('debug console');
        console.log('[sage] Game paused');
    });

    dc.expose('resume', () =>
    {
        gm.resume('debug console');
        console.log('[sage] Game resumed');
    });

    dc.expose('slow', (factor : number) =>
    {
        (ge.renderEngine as unknown as Record<string, unknown>).timeStep = (1 / 60) / factor;
        console.log(`[sage] Time scale set to ${ factor }x (timeStep=${ (1 / 60) / factor })`);
    });
}

//----------------------------------------------------------------------------------------------------------------------
